import React, { useState, useEffect, useRef } from 'react';
import { Printer, Download, ArrowLeft, RefreshCw } from 'lucide-react';

const PurchaseOrderAmendment = () => {
  const printRef = useRef();
  const [amendmentData, setAmendmentData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amendmentId, setAmendmentId] = useState(null);

  // Company data
  const companyData = {
    name: "Nissi Infotech",
    subtitle: "Nissi",
    tagline: "CIVIL CONSTRUCTION",
    address: "3/42-12, Nagappa colony, kavundampalayam, Salem-636030, Tamil Nadu, India",
    phone: "Phone No. 9003744644",
    email: "Email: infraconsoft-implementation@nissiinfotech.in"
  };

  // Extract ID from URL and fetch data
  useEffect(() => {
    const extractIdFromUrl = () => {
      // Try to extract ID from different URL patterns
      const path = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      
      // Method 1: From URL path (e.g., /purchase-order-amendment/1)
      const pathSegments = path.split('/');
      const pathId = pathSegments[pathSegments.length - 1];
      
      // Method 2: From query parameter (e.g., ?id=1)
      const queryId = searchParams.get('id');
      
      // Method 3: From hash (e.g., #1)
      const hashId = window.location.hash.replace('#', '');
      
      // Use the first valid ID found
      const id = queryId || pathId || hashId || '1'; // Default to 1 if no ID found
      
      if (!isNaN(id) && id !== '') {
        setAmendmentId(id);
        fetchAmendmentData(id);
      } else {
        setError('Invalid amendment ID');
        setLoading(false);
      }
    };

    extractIdFromUrl();
  }, []);

  // Fetch data from API
  const fetchAmendmentData = async (id) => {
    try {
      setLoading(true);
      setError(null);

      // Replace with your actual API endpoint
      const apiUrl = `https://www.nissi-india.com/api/purchaseOrder_reportPrint2/${id}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const res = await response.json();

// Sometimes API sends array, sometimes object.
// Normalize it into an array
const rawData = Array.isArray(res.data) ? res.data : [res.data];

// Main header info from the first record
const header = rawData[0];

if (header) {
  setAmendmentData({
    to: header.fld_suppliername || header.vendor || 'Unknown Supplier',
    address: header.fld_address1 || header.supplier_address || '',
    pin: header.fld_ZipCode || header.pincode || '',
    orderNo: header.fld_POno || header.order_no || header.po_no || '',
    orderedDate: header.fld_date || header.ordered_date || header.order_date || '',
    ourRefNo: header.fld_Refno || header.ref_no || header.reference_no || ''
  });
}

// Items → map all records
const transformedItems = rawData.map((item, index) => ({
  sno: index + 1,
  description: item.fld_itemname || item.item_name || 'N/A',
  partNumber: item.fld_itemPartNo || item.part_number || '',
  qty: item.fld_qty || item.quantity || 0,
  unitPrice: item.fld_ppoprice || item.unit_price || 0,
  totalAmount: item.total || item.total_amount || 0
}));

setItems(transformedItems);

      
    } catch (err) {
      console.error('Error fetching amendment data:', err);
      setError(`Failed to fetch data: ${err.message}`);
      
      // Set sample data for demonstration on error
      setAmendmentData({
        to: 'sindhu',
        address: 'Oman-, Coimbatore,Tamil Nadu,India',
        pin: '',
        orderNo: 'HEAD-OFFICE/OP/2022/0001',
        orderedDate: '28/05/2022',
        ourRefNo: ''
      });
      
      setItems([
        {
          sno: 1,
          description: '20 MM Lime stone',
          partNumber: '675953',
          qty: '5.00',
          unitPrice: '1,500.00',
          totalAmount: '7,500.00'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate grand total
const calculateGrandTotal = () => {
  const total = items.reduce((sum, item) => {
    // If it’s a number, use it directly, if string → clean it
    const amount =
      typeof item.totalAmount === "string"
        ? parseFloat(item.totalAmount.replace(/[₹,]/g, "")) || 0
        : parseFloat(item.totalAmount) || 0;

    return sum + amount;
  }, 0);

  return total.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};


  // Handle refresh
  const handleRefresh = () => {
    if (amendmentId) {
      fetchAmendmentData(amendmentId);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    console.log("Download functionality would be implemented here");
  };

  const handleBack = () => {
    console.log("Navigate back");
  };

  return (
    <div className="min-h-screen bg-white print:bg-white">
      {/* Print styles */}
      <style jsx>{`
        @media print {
          @page {
            margin: 0.5in;
            size: A4;
          }
          
          .no-print {
            display: none !important;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          * {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
        }
      `}</style>

      {/* Header Controls - Hidden in print */}
      <div className="print:hidden bg-gray-50 shadow-sm border-b p-4 no-print">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowLeft size={18} />
                Back
              </button>
              
              {/* API Status Indicator */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Amendment ID: {amendmentId}</span>
                {loading && <span className="text-blue-600">Loading...</span>}
                {error && <span className="text-red-600">API Error</span>}
                {!loading && !error && (
                  <span className="text-green-600">
                    Loaded {items.length} items
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded text-sm transition-colors disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white hover:bg-green-700 rounded text-sm transition-colors"
              >
                <Download size={16} />
                PDF
              </button>
              
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-3 py-2 bg-gray-800 text-white hover:bg-gray-900 rounded text-sm transition-colors"
              >
                <Printer size={16} />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Content */}
      <div className="max-w-4xl mx-auto print:max-w-full print:mx-0 p-4 print:p-0">
        <div
          ref={printRef}
          className="bg-white print:bg-white border-2 border-black print:border-2 print:border-black"
        >
          {/* Company Header */}
          <div className="border-b-2 border-black p-4">
            <div className="flex items-start gap-4">
              {/* Company Logo */}
              <div className="w-20 h-16 bg-black flex items-center justify-center flex-shrink-0">
                <div className="text-white text-xs font-bold text-center">
                  <div>NISSI</div>
                  <div>INFOTECH</div>
                  <div>PVT LTD</div>
                </div>
              </div>
              
              {/* Company Details */}
              <div className="flex-1 text-center">
                <h1 className="text-xl font-bold text-black mb-1">{companyData.name}</h1>
                <p className="text-sm font-semibold text-black mb-1">{companyData.subtitle}</p>
                <p className="text-sm font-bold text-black mb-2">{companyData.tagline}</p>
                <p className="text-xs text-black mb-1">{companyData.address}</p>
                <div className="text-xs text-black">
                  <div>{companyData.phone}</div>
                  <div>{companyData.email}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Document Title */}
          <div className="text-center py-3 border-b-2 border-black">
            <h2 className="text-lg font-bold underline text-black">Purchase Order Amendment</h2>
          </div>

          {/* Loading/Error States */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-2"></div>
                <p className="text-sm">Loading amendment data...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center py-8 print:hidden">
              <div className="text-center text-red-600">
                <p className="text-sm mb-2">Error loading data</p>
                <p className="text-xs mb-2">{error}</p>
                <button 
                  onClick={handleRefresh}
                  className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Amendment Content */}
          {!loading && amendmentData && (
            <>
              {/* Order Details */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-8 mb-6">
                  <div>
                    <div className="mb-2">
                      <span className="font-semibold text-sm">To</span>
                    </div>
                    <div className="text-sm mb-1 font-semibold">{amendmentData.to}</div>
                    <div className="text-sm mb-1">{amendmentData.address}</div>
                    {amendmentData.pin && <div className="text-sm mb-1">Pin: {amendmentData.pin}</div>}
                  </div>
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="font-semibold text-sm w-28">Order No</span>
                      <span className="text-sm">{amendmentData.orderNo}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold text-sm w-28">Ordered Date</span>
                      <span className="text-sm">{amendmentData.orderedDate}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold text-sm w-28">Our Ref No</span>
                      <span className="text-sm">{amendmentData.ourRefNo}</span>
                    </div>
                  </div>
                </div>

                {/* Items Request */}
                <div className="border-t border-black pt-4">
                  <p className="text-sm mb-4">Dear sir, please supply the following items subject to all items and conditions mentioned below</p>
                  
                  {/* Items Table */}
                  <table className="w-full border-collapse border border-black">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-black px-2 py-2 text-xs font-bold text-center">S.no</th>
                        <th className="border border-black px-2 py-2 text-xs font-bold text-center">Description</th>
                        <th className="border border-black px-2 py-2 text-xs font-bold text-center">Part Number</th>
                        <th className="border border-black px-2 py-2 text-xs font-bold text-center">Qty</th>
                        <th className="border border-black px-2 py-2 text-xs font-bold text-center">Unit Price</th>
                        <th className="border border-black px-2 py-2 text-xs font-bold text-center">Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 && !loading ? (
                        <tr>
                          <td colSpan="6" className="border border-black text-center py-8 text-gray-500">
                            No items found
                          </td>
                        </tr>
                      ) : (
                        items.map((item) => (
                          <tr key={item.sno}>
                            <td className="border border-black px-2 py-2 text-xs text-center">{item.sno}</td>
                            <td className="border border-black px-2 py-2 text-xs">{item.description}</td>
                            <td className="border border-black px-2 py-2 text-xs text-center">{item.partNumber}</td>
                            <td className="border border-black px-2 py-2 text-xs text-right">{item.qty}</td>
                            <td className="border border-black px-2 py-2 text-xs text-right">₹{item.unitPrice}</td>
                            <td className="border border-black px-2 py-2 text-xs text-right">₹{item.totalAmount}</td>
                          </tr>
                        ))
                      )}
                      
                      {/* Empty rows for consistent spacing */}
                      {items.length > 0 && [...Array(Math.max(0, 5 - items.length))].map((_, index) => (
                        <tr key={`empty-${index}`}>
                          <td className="border border-black px-2 py-2 h-8"></td>
                          <td className="border border-black px-2 py-2"></td>
                          <td className="border border-black px-2 py-2"></td>
                          <td className="border border-black px-2 py-2"></td>
                          <td className="border border-black px-2 py-2"></td>
                          <td className="border border-black px-2 py-2"></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Grand Total */}
                  {items.length > 0 && (
                    <div className="flex justify-end mt-2">
                      <div className="border border-black px-4 py-2 bg-gray-50">
                        <span className="text-sm font-bold">Grand Total: ₹{calculateGrandTotal()}</span>
                      </div>
                    </div>
                  )}

                  {/* Terms and Conditions */}
                  <div className="mt-6 border border-black">
                    <div className="bg-gray-50 px-3 py-2 border-b border-black">
                      <h4 className="text-sm font-bold text-center">TERMS AND CONDITIONS</h4>
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="flex text-xs">
                        <span className="w-40 font-semibold">Tax</span>
                        <span className="mr-2">:</span>
                        <span>Inclusive of All Taxes</span>
                      </div>
                      <div className="flex text-xs">
                        <span className="w-40 font-semibold">Delivery & Installation</span>
                        <span className="mr-2">:</span>
                        <span>On or Before 28/05/2022</span>
                      </div>
                      <div className="flex text-xs">
                        <span className="w-40 font-semibold">Freight</span>
                        <span className="mr-2">:</span>
                        <span>0.00</span>
                      </div>
                      <div className="flex text-xs">
                        <span className="w-40 font-semibold">Delivery Method</span>
                        <span className="mr-2">:</span>
                        <span>road</span>
                      </div>
                      <div className="flex text-xs">
                        <span className="w-40 font-semibold">Payment</span>
                        <span className="mr-2">:</span>
                        <span>cash</span>
                      </div>
                      <div className="flex text-xs">
                        <span className="w-40 font-semibold">Other instruction</span>
                        <span className="mr-2">:</span>
                        <span>Please Mention our TIN No 545546416 in your invoice</span>
                      </div>
                    </div>
                  </div>

                  {/* Signature Section */}
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="border border-black h-24">
                      <div className="p-2 text-xs">
                        <div>Received and Accepted Terms and Conditions</div>
                      </div>
                    </div>
                    <div className="border border-black h-24">
                      <div className="p-2">
                        <div className="text-xs mb-2">for</div>
                        <div className="border border-black text-center py-1 text-sm font-bold">
                          {companyData.name}
                        </div>
                        <div className="text-right text-xs mt-2">Authorized Signatory</div>
                      </div>
                      <div className="text-xs px-2">Authorized Signatory (with seal)</div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="border-t-2 border-black mt-auto">
            <div className="p-2 text-center">
              <div className="text-xs underline">
                Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Debug Panel - Hidden in print */}
      <div className="print:hidden mt-8 p-4 bg-gray-100 rounded no-print max-w-4xl mx-auto">
        <h3 className="font-bold mb-2">Debug Info:</h3>
        <div className="text-sm space-y-1">
          <p><strong>Amendment ID:</strong> {amendmentId}</p>
          <p><strong>API URL:</strong> https://www.nissi-india.com/api/getPurchaseOrderAmendment/{amendmentId}</p>
          <p><strong>Status:</strong> {loading ? 'Loading...' : error ? 'Error' : 'Success'}</p>
          <p><strong>Items Count:</strong> {items.length}</p>
          {error && <p><strong>Error:</strong> {error}</p>}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderAmendment;